import {all, takeLatest} from '@redux-saga/core/effects';
import {SicCodeDto} from 'BOSTools/api';
import {ApplicationStatusDto, CaseNameDto, LifeDetailDto} from 'BOSTools/api/api';
import bosToolsApi from 'BOSTools/api/bosToolsApi';
import {
  BOSToolsActionTypes,
  changeApplicationStatus,
  changeCaseAgentNumbers,
  changeCaseNames,
  changeGrandfatheringData,
  changeLifeDetails,
  changeSicCodes,
  getApplicationStatusBySceneID,
  getCaseAgentNumbersByCaseNumber,
  getCaseNamesByCaseNumber,
  getGrandfatheringData,
  getLifeDetailsByAccountNumber,
  getSicCodesByCaseNumber,
  updateApplicationStatus,
  updateCaseAgentNumber,
  updateCaseName,
  updateSicCode,
  updateWrittenToL70Flag,
} from 'BOSTools/ducks/bosTools';
import {CaseAgentNumber} from 'BOSTools/models/CaseAgentNumber';
import {CaseName} from 'BOSTools/models/CaseName';
import {GrandfatheringData} from 'BOSTools/models/GrandfatherData';
import {ValidGuid} from 'BOSTools/models/Guid';
import {LifeDetail} from 'BOSTools/models/LifeDetail';
import {SicCode, ValidSicCodeValue} from 'BOSTools/models/SicCode';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult, Loaded, Loading, LoadingAdditional, ServerError} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {call, put, select} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';

function* getApplicationStatusBySceneIDApi(action: ReturnType<typeof getApplicationStatusBySceneID>) {
  yield put(changeApplicationStatus(new Loading()));
  const result: AsyncResult<ApplicationStatusDto> = yield call(
    bosToolsApi.getApplicationStatusBySceneID,
    action.payload.sceneID.value!
  );

  yield pipe(
    result,
    E.map(value => new Loaded({...value, sceneID: ValidGuid.create(value.sceneID)})),
    E.fold(
      value => put(changeApplicationStatus(value)),
      value => put(changeApplicationStatus(value))
    )
  );
}

function* getApplicationStatusBySceneIDSaga() {
  yield takeLatest(BOSToolsActionTypes.GET_APPLICATION_STATUS_BY_SCENE_ID, getApplicationStatusBySceneIDApi);
}

function* updateApplicationStatusApi(action: ReturnType<typeof updateApplicationStatus>) {
  yield put(changeApplicationStatus(new Loading()));
  const result = yield call(bosToolsApi.updateApplicationStatus, action.payload.sceneID.value, action.payload.status);

  if (E.isRight(result)) {
    yield put(createSnackbar('Update successful'));
  } else {
    const snackbarAction = createSnackbar(
      (result.left instanceof ServerError && result.left.errorDetails) || result.left.message
    );

    yield put(snackbarAction);
  }
  yield put(changeApplicationStatus(new Loaded({sceneID: action.payload.sceneID, status: action.payload.status})));
}

function* updateApplicationStatusSaga() {
  yield takeLatest(BOSToolsActionTypes.UPDATE_APPLICATION_STATUS, updateApplicationStatusApi);
}

function* getCaseAgentNumbersByCaseNumberApi(action: ReturnType<typeof getCaseAgentNumbersByCaseNumber>) {
  yield put(changeCaseAgentNumbers(new Loading()));
  const result: AsyncResult<CaseAgentNumber[]> = yield call(
    bosToolsApi.getCaseAgentNumbersByCaseNumber,
    action.payload.caseNumber
  );

  yield pipe(
    result,
    E.map(value => new Loaded(value)),
    E.fold(
      value => put(changeCaseAgentNumbers(value)),
      value => put(changeCaseAgentNumbers(value))
    )
  );
}

function* getCaseAgentNumbersByCaseNumberSaga() {
  yield takeLatest(BOSToolsActionTypes.GET_CASE_AGENT_NUMBERS_BY_CASE_NUMBER, getCaseAgentNumbersByCaseNumberApi);
}

function* updateCaseAgentNumberApi(action: ReturnType<typeof updateCaseAgentNumber>) {
  const {caseAgentNumbers} = yield select((state: ReducerState) => state.bostools);

  yield put(changeCaseAgentNumbers(new Loading()));
  const result = yield call(bosToolsApi.updateCaseAgentNumber, action.payload.caseAgentNumber);

  if (E.isRight(result)) {
    yield put(createSnackbar('Update successful'));
  } else {
    const snackbarAction = createSnackbar(
      (result.left instanceof ServerError && result.left.errorDetails) || result.left.message
    );

    yield put(snackbarAction);
  }

  // Find the one that we are updating and replace it with the updated data
  const updatedCaseAgentNumbers = (caseAgentNumbers as Loaded<CaseAgentNumber[]>).Data.map(can =>
    can.caseID === action.payload.caseAgentNumber.caseID ? action.payload.caseAgentNumber : can
  );
  yield put(changeCaseAgentNumbers(new Loaded<CaseAgentNumber[]>(updatedCaseAgentNumbers)));
}

function* updateCaseAgentNumberSaga() {
  yield takeLatest(BOSToolsActionTypes.UPDATE_CASE_AGENT_NUMBERS, updateCaseAgentNumberApi);
}

function* getGrandfatheringDataFromApi(action: ReturnType<typeof getGrandfatheringData>) {
  if (action.payload.pageOption) {
    yield put(changeGrandfatheringData(new LoadingAdditional(action.payload.pageOption)));
  } else {
    yield put(changeGrandfatheringData(new Loading(action.payload.pageOption)));
  }

  const result: AsyncResult<Page<GrandfatheringData>> = yield action.payload.pageOption
    ? call(mergeWithNextPage, {existingPage: action.payload.pageOption})
    : call(bosToolsApi.getGrandfatheringData);

  yield pipe(
    result,
    E.map(value => new Loaded(value)),
    E.fold(
      value => put(changeGrandfatheringData(value)),
      value => put(changeGrandfatheringData(value))
    )
  );
}

function* getGrandfatheringDataSaga() {
  yield takeLatest(BOSToolsActionTypes.GET_GRANDFATHERING_DATA_REPORT, getGrandfatheringDataFromApi);
}

function* getSicCodesByCaseNumberApi(action: ReturnType<typeof getSicCodesByCaseNumber>) {
  yield put(changeSicCodes(new Loading()));
  const result: AsyncResult<SicCodeDto[]> = yield call(bosToolsApi.getSicCodesByCaseNumber, action.payload.caseNumber);

  yield pipe(
    result,
    E.map(value => new Loaded(value)),
    E.fold(
      value => put(changeSicCodes(value)),
      value =>
        put(
          changeSicCodes(
            new Loaded<SicCode[]>(value.Data.map(d => ({...d, sicCodeValue: ValidSicCodeValue.create(d.sicCode)})))
          )
        )
    )
  );
}

function* getSicCodesByCaseNumberSaga() {
  yield takeLatest(BOSToolsActionTypes.GET_SIC_CODES_BY_CASE_NUMBER, getSicCodesByCaseNumberApi);
}

function* updateSicCodeApi(action: ReturnType<typeof updateSicCode>) {
  const {sicCodes} = yield select((state: ReducerState) => state.bostools);
  yield put(changeSicCodes(new Loading()));

  const result = yield call(
    bosToolsApi.updateSicCode,
    action.payload.sicCode.groupID!,
    action.payload.sicCode.sicCodeValue.value!
  );

  if (E.isRight(result)) {
    yield put(createSnackbar('Update successful'));
  } else {
    yield put(createSnackbar((result.left instanceof ServerError && result.left.errorDetails) || result.left.message));
  }

  // Find the one that we are updating and replace it with the updated data
  const updatedSicCodes = (sicCodes as Loaded<SicCode[]>).Data.map(sc =>
    sc.groupID === action.payload.sicCode.groupID ? action.payload.sicCode : sc
  );
  yield put(changeSicCodes(new Loaded<SicCode[]>(updatedSicCodes)));
}

function* updateSicCodeSaga() {
  yield takeLatest(BOSToolsActionTypes.UPDATE_SIC_CODE, updateSicCodeApi);
}

function* getCaseNamesByCaseNumberApi(action: ReturnType<typeof getCaseNamesByCaseNumber>) {
  yield put(changeCaseNames(new Loading()));
  const result: AsyncResult<CaseNameDto[]> = yield call(
    bosToolsApi.getCaseNamesByCaseNumber,
    action.payload.caseNumber
  );

  yield pipe(
    result,
    E.map(value => new Loaded(value)),
    E.fold(
      value => put(changeCaseNames(value)),
      value => put(changeCaseNames(value))
    )
  );
}

function* getCaseNamesByCaseNumberSaga() {
  yield takeLatest(BOSToolsActionTypes.GET_CASE_NAMES_BY_CASE_NUMBER, getCaseNamesByCaseNumberApi);
}

function* updateCaseNameApi(action: ReturnType<typeof updateCaseName>) {
  const {caseNames} = yield select((state: ReducerState) => state.bostools);
  yield put(changeCaseNames(new Loading()));

  const result = yield call(
    bosToolsApi.updateCaseName,
    action.payload.caseName.groupID!,
    action.payload.caseName.caseName!
  );

  if (E.isRight(result)) {
    yield put(createSnackbar('Update successful'));
  } else {
    yield put(createSnackbar((result.left instanceof ServerError && result.left.errorDetails) || result.left.message));
  }

  // Find the one that we are updating and replace it with the updated data
  const updatedCaseNames = (caseNames as Loaded<CaseName[]>).Data.map(cn =>
    cn.groupID === action.payload.caseName.groupID ? action.payload.caseName : cn
  );
  yield put(changeCaseNames(new Loaded<CaseName[]>(updatedCaseNames)));
}

function* updateCaseNameSaga() {
  yield takeLatest(BOSToolsActionTypes.UPDATE_CASE_NAME, updateCaseNameApi);
}

function* getLifeDetailsByAccountNumberApi(action: ReturnType<typeof getLifeDetailsByAccountNumber>) {
  yield put(changeLifeDetails(new Loading()));
  const result: AsyncResult<LifeDetailDto[]> = yield call(
    bosToolsApi.getLifeDetailsByAccountNumber,
    action.payload.accountNumber
  );

  yield pipe(
    result,
    E.map(value => new Loaded(value)),
    E.fold(
      value => put(changeLifeDetails(value)),
      value => put(changeLifeDetails(value))
    )
  );
}

function* getLifeDetailsByAccountNumberSaga() {
  yield takeLatest(BOSToolsActionTypes.GET_LIFE_DETAILS_BY_ACCOUNT_NUMBER, getLifeDetailsByAccountNumberApi);
}

function* updateWrittenToL70FlagApi(action: ReturnType<typeof updateWrittenToL70Flag>) {
  const {lifeDetails} = yield select((state: ReducerState) => state.bostools);
  yield put(changeLifeDetails(new Loading()));

  const result = yield call(
    bosToolsApi.updateWrittenToL70Flag,
    action.payload.lifeDetail.employerID!,
    action.payload.lifeDetail.writtenToL70!
  );

  if (E.isRight(result)) {
    yield put(createSnackbar('Update successful'));
  } else {
    yield put(createSnackbar((result.left instanceof ServerError && result.left.errorDetails) || result.left.message));
  }

  // Find the one that we are updating and replace it with the updated data
  const updatedLifeDetails = (lifeDetails as Loaded<LifeDetail[]>).Data.map(ld =>
    ld.employerID === action.payload.lifeDetail.employerID ? action.payload.lifeDetail : ld
  );
  yield put(changeLifeDetails(new Loaded<LifeDetail[]>(updatedLifeDetails)));
}

function* updateLifeDetailSaga() {
  yield takeLatest(BOSToolsActionTypes.UPDATE_WRITTEN_TO_L70_FLAG, updateWrittenToL70FlagApi);
}

export default function*() {
  yield all([
    getApplicationStatusBySceneIDSaga(),
    updateApplicationStatusSaga(),
    getCaseAgentNumbersByCaseNumberSaga(),
    updateCaseAgentNumberSaga(),
    getSicCodesByCaseNumberSaga(),
    updateSicCodeSaga(),
    getGrandfatheringDataSaga(),
    updateCaseNameSaga(),
    getCaseNamesByCaseNumberSaga(),
    updateLifeDetailSaga(),
    getLifeDetailsByAccountNumberSaga(),
  ]);
}
